import { useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { AUDIO_INDICATOR_COLOR, useAudioIndicatorTone } from '@renderer/hooks/audio/use-audio-indicator-tone'
import { useIsAudioNotePaused } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useAudioInputIdle } from '@renderer/hooks/audio/use-audio-input-level'
import { useNoAudio } from '@renderer/hooks/audio/use-no-audio'
import { unsecureRandom } from '@shared/utils/random'

const NUM_BARS = 3
const SIDE_BASE_HEIGHT = 4
const SIDE_MAX_HEIGHT = 8
const MIDDLE_BASE_HEIGHT = 4
const MIDDLE_MAX_HEIGHT = 16
const BASE_HEIGHTS = [SIDE_BASE_HEIGHT, MIDDLE_BASE_HEIGHT, SIDE_BASE_HEIGHT]

interface WaveformProps {
  className?: string
}

export function AudioWaveform({ className }: WaveformProps) {
  const noAudio = useNoAudio()
  // Only the idle/active transition needs a render here — the bar magnitudes are
  // read from the ref inside the 1 s loop below, so subscribing to the raw ~10 Hz
  // level meant ~10 renders/sec for ~1 visible change/sec.
  const { isIdle, levelRef: inputLevelRef } = useAudioInputIdle()
  const isPaused = useIsAudioNotePaused()
  const tone = useAudioIndicatorTone()
  const [barHeights, setBarHeights] = useState<Array<number>>(BASE_HEIGHTS)
  const updateIntervalRef = useRef<NodeJS.Timeout>()
  const isPausedRef = useRef(isPaused)

  const generateBarHeights = (currentLevel: number) => {
    return Array(NUM_BARS)
      .fill(0)
      .map((_, index) => {
        const isMiddle = index === 1
        const baseHeight = isMiddle ? MIDDLE_BASE_HEIGHT : SIDE_BASE_HEIGHT
        const maxHeight = isMiddle ? MIDDLE_MAX_HEIGHT : SIDE_MAX_HEIGHT

        if (currentLevel < 0.1) {
          return baseHeight
        }

        const normalizedLevel = Math.min(1, currentLevel / 3)
        let height = baseHeight + (maxHeight - baseHeight) * normalizedLevel

        // Add randomization between 50% and 100% of the current height
        const randomFactor = 0.5 + unsecureRandom() * 0.5
        height = Math.max(baseHeight, Math.min(maxHeight, height * randomFactor))

        return height
      })
  }

  useEffect(() => {
    isPausedRef.current = isPaused

    clearInterval(updateIntervalRef.current)

    if (isPaused || isIdle) {
      setBarHeights(BASE_HEIGHTS)
      return
    }

    setBarHeights(generateBarHeights(inputLevelRef.current))

    updateIntervalRef.current = setInterval(() => {
      if (isPausedRef.current || inputLevelRef.current < 0.1) {
        clearInterval(updateIntervalRef.current)
        setBarHeights(BASE_HEIGHTS)
        return
      }
      setBarHeights(generateBarHeights(inputLevelRef.current))
    }, 1000)

    return () => {
      clearInterval(updateIntervalRef.current)
    }
  }, [isPaused, isIdle])

  return (
    <div className={twMerge('flex h-[20px] shrink-0 items-center gap-0.5', AUDIO_INDICATOR_COLOR[tone], className)}>
      {barHeights.map((height, index) => {
        const isMiddle = index === 1
        const baseHeight = isMiddle ? MIDDLE_BASE_HEIGHT : SIDE_BASE_HEIGHT
        const scaleY = height / baseHeight
        return (
          <div
            key={`waveform-bar-${index}`}
            // 150ms, not 500: bars update once a second, and the overlay is a
            // full-screen transparent window the compositor redraws for every
            // frame of the glide — 500ms kept it drawing half of every second.
            className={twMerge(
              'w-[2px] origin-center rounded-full bg-current transition-transform duration-150 ease-out',
              tone === 'live' && noAudio && 'opacity-30',
            )}
            style={{
              height: `${baseHeight}px`,
              transform: `scaleY(${isPaused ? 1 : scaleY})`,
            }}
          />
        )
      })}
    </div>
  )
}
